import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Select, Space, Typography } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import { invalidateBusinessResources } from "../../utils/businessResourceFreshness";
import { numberingHeaders, numberingErrorMessage, useInvoiceNumbering } from "../../hooks/useInvoiceNumbering";

export default function InvoiceNumberingSettings({ companyId, warehouseId = null, active = true }) {
  const { user } = useAuth();
  const client = useQueryClient();
  const resource = useInvoiceNumbering({ companyId, warehouseId, active });
  const [mode, setMode] = useState(warehouseId ? "inherit" : "automatic");
  const [baseline, setBaseline] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const scopeKey = `${companyId}:${warehouseId}:${user?.id}:${active}`;
  const current = useRef(scopeKey); current.current = scopeKey;
  useEffect(() => { current.current = scopeKey; setBaseline(null); setDirty(false); setFeedback(null); setSaving(false);
    return () => { current.current = null; }; }, [scopeKey]);
  useEffect(() => {
    if (resource.status !== "verified" || dirty) return;
    setBaseline(resource.data.version);
    setMode(warehouseId ? resource.data.warehouse_mode : resource.data.company_mode);
  }, [resource.status, resource.data, warehouseId, dirty]);
  if (!companyId) return null;
  const conflict = dirty && baseline !== null && resource.data && baseline !== resource.data.version;
  const reload = async () => {
    try {
      const fresh = await resource.refresh({ force: true });
      if (current.current !== scopeKey) return;
      setBaseline(fresh.version); setMode(warehouseId ? fresh.warehouse_mode : fresh.company_mode); setDirty(false); setFeedback(null);
    } catch (error) { if (current.current === scopeKey) setFeedback({ type: "error", message: numberingErrorMessage(error) }); }
  };
  const save = async () => {
    if (saving) return;
    setSaving(true); setFeedback(null);
    try {
      const fresh = await resource.refresh({ force: true });
      if (current.current !== scopeKey) return;
      if (fresh.version !== baseline) throw new Error("Ce réglage a changé depuis un autre poste. Rechargez-le avant d'enregistrer.");
      await apiClient.put("/api/invoice-numbering/settings", { company_id: companyId, ...(warehouseId ? { warehouse_id: warehouseId } : {}), mode, expected_version: baseline }, { headers: numberingHeaders(companyId) });
      if (current.current !== scopeKey) return;
      setDirty(false); setFeedback({ type: "success", message: "Réglage de numérotation enregistré." });
      await invalidateBusinessResources(client, { userId: user?.id, companyId, warehouseId, moduleScope: "commercial" }, "invoice-numbering-settings");
    } catch (error) { if (current.current === scopeKey) setFeedback({ type: "error", message: numberingErrorMessage(error) }); }
    finally { if (current.current === scopeKey) setSaving(false); }
  };
  return <section aria-label="Numérotation des factures" style={{ marginBlock: 16 }}>
    <Typography.Paragraph strong>Numérotation des factures de vente et POS</Typography.Paragraph>
    <Typography.Paragraph type="secondary">{warehouseId
      ? "Cet établissement peut suivre le réglage de l'entreprise ou conserver l'automatique. L'entreprise doit autoriser la saisie manuelle."
      : "L'automatique reste disponible. L'autorisation manuelle permet aux utilisateurs habilités de choisir ou corriger un numéro, avec historique."}</Typography.Paragraph>
    <Space direction="vertical" style={{ width: "100%" }}>
      <Select aria-label="Mode de numérotation des factures" value={mode} style={{ width: "100%" }}
        disabled={!resource.data?.can_manage || !resource.data?.schema_available || resource.status !== "verified" || saving}
        onChange={value => { setMode(value); setDirty(true); setFeedback(null); }} options={[
          ...(warehouseId ? [{ value: "inherit", label: "Suivre le réglage de l'entreprise" }] : []),
          { value: "automatic", label: "Numérotation automatique" },
          { value: "manual_allowed", label: "Saisie et correction manuelles autorisées", disabled: Boolean(warehouseId && resource.data?.company_mode !== "manual_allowed") },
        ]} />
      {resource.data && <Typography.Text type="secondary">{resource.data.manual_allowed ? "Saisie manuelle autorisée, sous réserve des permissions de l'utilisateur." : "Numérotation automatique active."}</Typography.Text>}
      {resource.data && !resource.data.schema_available && <Alert type="info" showIcon message="La saisie manuelle sera disponible après la mise à jour de la base de données." />}
      {resource.data && !resource.data.can_manage && <Typography.Text type="secondary">La permission de modification de cet établissement ou de cette entreprise est requise.</Typography.Text>}
      {(conflict || resource.error) && <Alert type="warning" showIcon message={conflict ? "Ce réglage a changé depuis un autre poste. Votre choix est conservé." : "Impossible d'actualiser ce réglage. Votre choix est conservé."} />}
      {feedback && <Alert showIcon type={feedback.type} message={feedback.message} />}
      <Space wrap>
        <Button htmlType="button" loading={saving} disabled={!dirty || conflict || resource.status !== "verified" || !resource.data?.can_manage || !resource.data?.schema_available} onClick={save}>Enregistrer la numérotation</Button>
        <Button htmlType="button" disabled={saving} loading={resource.loading} onClick={() => void reload()}>Recharger le réglage</Button>
      </Space>
    </Space>
  </section>;
}
